import React, { useState } from 'react';
import { ArrowLeft, Palette, User, Users, AlertTriangle } from 'lucide-react';

const SettingsPage = ({
  user,
  subscription,
  loading,
  uploadingLogo,
  brandingForm,
  setBrandingForm,
  teamMembers,
  teamStatus,
  setCurrentView,
  setShowInviteModal,
  handleLogoUpload,
  handleSaveBranding,
  handleRemoveTeamMember,
  handleDeleteAccount,
  isFeatureAvailable,
  API_URL
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const onDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    const result = await handleDeleteAccount(deletePassword);
    if (!result?.success) {
      setDeleteError(result?.error || 'Failed to delete account');
      setDeleteLoading(false);
    }
  };
  return (
    <div className="settings-view">
      <div className="page-header">
        <h2>Settings</h2>
        <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
      </div>

      <div className="settings-section">
        <h3><Palette size={20} style={{marginRight: '8px'}} />Branding (PDF & Reports)</h3>
        <p className="section-description">Customize how your meal plans look when exported as PDFs for your clients.</p>
        
        {subscription?.plan === 'starter' ? (
          <div className="upgrade-prompt">
            <p>Custom branding is available on Pro and Unlimited plans.</p>
            <button className="btn-primary" onClick={() => setCurrentView('billing')}>
              Upgrade to Pro
            </button>
          </div>
        ) : (
          <div className="branding-form">
            <div className="form-group">
              <label>Company Logo</label>
              <p className="field-hint">Appears at the top of your PDF meal plans. Recommended size: 300x100px</p>
              <div className="logo-upload">
                {user.logo_url ? (
                  <img src={`${API_URL}${user.logo_url}`} alt="Logo" className="current-logo" />
                ) : (
                  <div className="no-logo">No logo uploaded</div>
                )}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
                {uploadingLogo && <span className="uploading-text">Uploading...</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label>Company Name</label>
              <p className="field-hint">Displayed as the header on your PDF meal plans</p>
              <input
                type="text"
                value={brandingForm.company_name}
                onChange={(e) => setBrandingForm({ ...brandingForm, company_name: e.target.value })}
                placeholder="Your Company Name"
              />
            </div>
            
            <div className="form-row color-row">
              <div className="form-group">
                <label>Primary Color</label>
                <p className="field-hint">Used for headings, titles and table headers in your PDFs</p>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    value={brandingForm.primary_color}
                    onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                  />
                  <span className="color-value">{brandingForm.primary_color}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Secondary Color</label>
                <p className="field-hint">Used for accents and highlights in your PDFs</p>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    value={brandingForm.secondary_color}
                    onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })}
                  />
                  <span className="color-value">{brandingForm.secondary_color}</span>
                </div>
              </div>
            </div>
            
            <button className="btn-primary" onClick={handleSaveBranding} disabled={loading}>
              {loading ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        )}
      </div>

      <div className="settings-section">
        <h3><User size={20} style={{marginRight: '8px'}} />Account</h3>
        <div className="account-info">
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Plan:</strong> {subscription?.plan_name || 'Unknown'}</p>
          <p><strong>Status:</strong> {subscription?.plan_status || 'Unknown'}</p>
        </div>
      </div>

      {/* Team Section (Unlimited only) */}
      {isFeatureAvailable('team_members') && !teamStatus?.is_team_member && (
        <div className="settings-section">
          <h3><Users size={20} style={{marginRight: '8px'}} />Team Members</h3>
          <p className="section-description">
            Add team members who can view clients and create meal plans. 
            Team members cannot access billing or settings.
          </p>
          
          <div className="team-header">
            <span className="team-count">
              {teamMembers.length} of 3 team members
            </span>
            {teamMembers.length < 3 && (
              <button 
                className="btn-primary btn-sm"
                onClick={() => setShowInviteModal(true)}
              >
                + Add Team Member
              </button>
            )}
          </div>
          
          {teamMembers.length === 0 ? (
            <div className="empty-state small">
              <p>No team members yet. Invite your first team member to collaborate.</p>
            </div>
          ) : (
            <div className="team-list">
              {teamMembers.map(member => (
                <div key={member.user_id} className="team-member-card">
                  <div className="member-info">
                    <span className="member-name">{member.name}</span>
                    <span className="member-email">{member.email}</span>
                  </div>
                  <button 
                    className="btn-danger btn-sm"
                    onClick={() => handleRemoveTeamMember(member.user_id, member.name)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Show notice for team members */}
      {teamStatus?.is_team_member && (
        <div className="settings-section">
          <h3><Users size={20} style={{marginRight: '8px'}} />Team Access</h3>
          <div className="team-member-notice">
            <p>You are a team member on <strong>{teamStatus.parent_name}</strong>'s account.</p>
            <p>You can view clients and create meal plans, but cannot manage billing or team settings.</p>
          </div>
        </div>
      )}

      {/* Danger Zone - Delete Account */}
      <div className="settings-section danger-zone" data-testid="delete-account-section">
        <h3><AlertTriangle size={20} style={{marginRight: '8px', color: '#e74c3c'}} />Danger Zone</h3>
        {!showDeleteConfirm ? (
          <div>
            <p className="section-description">Permanently delete your account and all associated data. This action cannot be undone.</p>
            <button 
              className="btn-danger"
              data-testid="delete-account-btn"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete My Account
            </button>
          </div>
        ) : (
          <div className="delete-confirm-card">
            <AlertTriangle size={24} color="#e74c3c" />
            <h4>This will permanently delete:</h4>
            <ul>
              <li>Your account and login credentials</li>
              <li>All client profiles and their data</li>
              <li>All meal plans and templates</li>
              <li>Your active subscription (if any)</li>
            </ul>
            <p><strong>This cannot be undone.</strong> Please enter your password to confirm:</p>
            <input
              type="password"
              data-testid="delete-password-input"
              value={deletePassword}
              onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
              placeholder="Enter your password"
              className="delete-password-input"
            />
            {deleteError && <p className="error-text">{deleteError}</p>}
            <div className="cancel-actions">
              <button 
                className="btn-danger"
                data-testid="confirm-delete-btn"
                onClick={onDeleteAccount}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
              <button 
                className="btn-secondary"
                onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
